import { IconButton, InputAdornment } from "@mui/material";
import MUISelect, {
  SelectChangeEvent,
  SelectProps,
} from "@mui/material/Select";
import React, { Children, useEffect, useState } from "react";

import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import { Icon } from "../Icon";
import Spinner from "../Spinner";
import clsx from "clsx";

export type OptionType = {
  value?: string | number;
  name?: string | number;
  children?: string | number;
};

interface SelectTypes extends Omit<SelectProps, "onChange"> {
  options?: OptionType[];
  onChange?: (param: OptionType, name?: string) => void;
  initialValue?: Partial<OptionType>;
  reference?: any;
  hasInitialValue?: boolean;
  onClear?: () => void;
  isError?: boolean;
  loading?: boolean;
  shrink?: boolean;
}

export function Select(props: SelectTypes) {
  const {
    onChange,
    options,
    initialValue,
    className,
    label,
    onClear,
    reference,
    hasInitialValue = true,
    name,
    size = "small",
    isError = false,
    loading = false,
    shrink,
    disabled,
    ...rest
  } = props;
  const [state, setState] = useState<Partial<OptionType> | any>(
    initialValue || undefined
  );

  function handleJsonStringify<ParamType>(params?: ParamType): string {
    if (params) {
      return JSON.stringify(params);
    }
    return "";
  }

  useEffect(() => {
    onChange?.(state, name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  function handleChange(_: SelectChangeEvent<unknown>, child: any) {
    setState(child?.props);
  }

  useEffect(() => {
    if (hasInitialValue) {
      if (handleJsonStringify(state) !== handleJsonStringify(initialValue)) {
        setState(initialValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  const handleClearValue = () => {
    setState(undefined); // Clear the selected value
    onClear?.();
  };

  return (
    <FormControl className={clsx(className, disabled && "cursor-not-allowed")}>
      <InputLabel
        size={size !== "small" ? "medium" : size}
        error={isError}
        id="select-label"
        shrink={shrink}
      >
        {label}
      </InputLabel>
      <MUISelect
        // labelId="select-label"
        className={clsx('"w-full', disabled && "cursor-not-allowed")}
        disabled={disabled}
        endAdornment={
          onClear &&
          state?.value && (
            <InputAdornment position="end" className="ml-4">
              <IconButton onClick={handleClearValue} size="small">
                <Icon icon="MdClear" />
              </IconButton>
            </InputAdornment>
          )
        }
        startAdornment={
          loading && (
            <InputAdornment position="start" className="ml-4">
              <Spinner size={14} />
            </InputAdornment>
          )
        }
        {...rest}
        {...{ label, name, size }}
        ref={reference}
        value={state?.value ?? ""}
        onChange={handleChange}
        error={isError}
        inputProps={{
          name,
          id: "select-label",
        }}
      >
        {Children.toArray(
          options?.map((item, index) => (
            <MenuItem value={item.value} id={`${index}`}>
              {item.name}
            </MenuItem>
          ))
        )}
      </MUISelect>
    </FormControl>
  );
}
